import { useState, useEffect, useCallback } from 'react';
import { MonitorManager } from './MonitorManager';
import type { ScreenDetailed } from './MonitorManager';
import type { MultiMonitorCapability, ChildWindowState, ScreenRole } from './types';

const EMPTY_CAPABILITY: MultiMonitorCapability = {
  apiSupported: false,
  permissionGranted: false,
  multipleScreens: false,
  available: false,
};

// Module-level singleton — shared across all consumers of the hook.
// This avoids accessing a ref during render (React compiler requirement).
let singletonManager: MonitorManager | null = null;
function getManager(): MonitorManager {
  if (!singletonManager) {
    singletonManager = new MonitorManager();
  }
  return singletonManager;
}

/**
 * React hook that provides multi-monitor management.
 *
 * Uses a module-level singleton MonitorManager so it's shared across
 * all consuming components and never accessed during render via ref.
 */
export function useMonitorManager() {
  const manager = getManager();

  const [capability, setCapability] = useState<MultiMonitorCapability>(EMPTY_CAPABILITY);
  const [screens, setScreens] = useState<ScreenDetailed[]>([]);
  const [childWindows, setChildWindows] = useState<ChildWindowState[]>([]);

  // Initial capability check
  useEffect(() => {
    manager.checkCapability().then(setCapability);
  }, [manager]);

  // Screen + child change subscriptions
  useEffect(() => {
    const unsubScreen = manager.onScreenChange(setScreens);
    const unsubChild = manager.onChildChange(setChildWindows);
    return () => { unsubScreen(); unsubChild(); };
  }, [manager]);

  // Close all child windows on unmount
  useEffect(() => {
    return () => { manager.destroy(); };
  }, [manager]);

  // Close child windows when the primary window unloads
  useEffect(() => {
    const handleUnload = () => { manager.closeAll(); };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [manager]);

  const requestPermission = useCallback(async () => {
    const granted = await manager.requestPermission();
    if (granted) {
      setScreens(manager.getScreens());
      setCapability(await manager.checkCapability());
    }
    return granted;
  }, [manager]);

  const openViewerOnScreen = useCallback(
    (screen: ScreenDetailed, studyInstanceUID: string, siteId: string, role?: ScreenRole, viewerId?: string, objKey?: string) => {
      return manager.openViewerOnScreen(screen, studyInstanceUID, siteId, role, viewerId, objKey);
    },
    [manager],
  );

  const closeWindow = useCallback(
    (windowId: string) => manager.closeWindow(windowId),
    [manager],
  );

  const closeAll = useCallback(() => manager.closeAll(), [manager]);

  return {
    capability,
    screens,
    childWindows,
    requestPermission,
    openViewerOnScreen,
    closeWindow,
    closeAll,
    manager,
  };
}
